"use client"
import { useEffect, useId, useRef } from "react"

type HubSpotFormOptions = {
  portalId: string
  formId: string
  target: string
}

type HubSpot = {
  forms: {
    create: (options: HubSpotFormOptions) => void
  }
}

declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Window {
    hbspt?: HubSpot
  }
}

const HUBSPOT_SRC = "https://js.hsforms.net/forms/shell.js"
const HUBSPOT_FRAME_SRC =
  "js.hsforms.net/ui-forms-embed-components-app/frame.html"
const TERMLY_REQUIRED_COOKIE_CATEGORY = "essential"
const HBSPT_TIMEOUT_MS = 10_000
const HBSPT_POLL_MS = 50

// Shared across all mounts so the script is only ever wired up once.
let hubspotPromise: Promise<HubSpot> | null = null
let hubspotDomPatchInstalled = false

// Resolve on `window.hbspt` actually existing rather than on the script's
// `load` event, since shell.js may define the global slightly after load.
const waitForHbspt = (loaded: Promise<void>): Promise<HubSpot> =>
  new Promise((resolve, reject) => {
    const start = Date.now()
    const check = () => {
      if (window.hbspt) return resolve(window.hbspt)
      if (Date.now() - start > HBSPT_TIMEOUT_MS) {
        return reject(
          new Error("HubSpot script loaded but hbspt is unavailable"),
        )
      }
      window.setTimeout(check, HBSPT_POLL_MS)
    }
    loaded.then(check, reject)
  })

const loadHubSpot = (): Promise<HubSpot> => {
  if (window.hbspt) return Promise.resolve(window.hbspt)
  if (hubspotPromise) return hubspotPromise

  const loaded = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${HUBSPOT_SRC}"]`,
    )

    // A pre-existing script may have already fired `load`, so don't depend on
    // that event; resolve immediately and let the hbspt poll decide readiness.
    if (existing) {
      resolve()
      return
    }

    const script = document.createElement("script")
    script.src = HUBSPOT_SRC
    script.async = true
    script.setAttribute("data-categories", TERMLY_REQUIRED_COOKIE_CATEGORY)
    script.addEventListener("load", () => resolve(), { once: true })
    script.addEventListener(
      "error",
      () => reject(new Error("Failed to load HubSpot script")),
      { once: true },
    )
    document.body.appendChild(script)
  })

  hubspotPromise = waitForHbspt(loaded).catch((err: unknown) => {
    // Clear the cache so a later mount can retry instead of reusing a rejection.
    hubspotPromise = null
    throw err
  })

  return hubspotPromise
}

const allowTermlyHubSpotFrame = (iframe: HTMLIFrameElement) => {
  const dataSrc = iframe.getAttribute("data-src")
  const currentSrc = iframe.getAttribute("src")

  iframe.setAttribute("data-categories", TERMLY_REQUIRED_COOKIE_CATEGORY)

  if (!currentSrc && dataSrc?.includes(HUBSPOT_FRAME_SRC)) {
    iframe.src = dataSrc.replace(/^http:/, "https:")
  }
}

const allowTermlyHubSpotEmbeds = (target: HTMLElement) => {
  target
    .querySelectorAll<HTMLIFrameElement>(
      `iframe[data-src*="${HUBSPOT_FRAME_SRC}"], iframe[src*="${HUBSPOT_FRAME_SRC}"]`,
    )
    .forEach(allowTermlyHubSpotFrame)
}

const allowTermlyHubSpotNode = (node: Node) => {
  if (!(node instanceof HTMLElement)) return

  if (node instanceof HTMLIFrameElement) {
    allowTermlyHubSpotFrame(node)
    return
  }

  allowTermlyHubSpotEmbeds(node)
}

const installHubSpotDomPatch = () => {
  if (hubspotDomPatchInstalled) return

  hubspotDomPatchInstalled = true

  const appendChild = Element.prototype.appendChild
  const insertBefore = Element.prototype.insertBefore

  Element.prototype.appendChild = function <T extends Node>(node: T): T {
    allowTermlyHubSpotNode(node)
    return appendChild.call(this, node) as T
  }

  Element.prototype.insertBefore = function <T extends Node>(
    node: T,
    child: Node | null,
  ): T {
    allowTermlyHubSpotNode(node)
    return insertBefore.call(this, node, child) as T
  }
}

export const HubspotForm = ({
  portalId,
  formId,
}: {
  portalId: string
  formId: string
}) => {
  const createdRef = useRef(false)
  const uid = useId()
  // useId() returns e.g. ":r0:" — strip colons so the value is a valid CSS id selector
  const elementId = `hbspt-form-${uid.replace(/:/g, "")}`

  useEffect(() => {
    let cancelled = false
    installHubSpotDomPatch()

    const target = document.getElementById(elementId)
    const observer = target
      ? new MutationObserver(() => allowTermlyHubSpotEmbeds(target))
      : null

    observer?.observe(target as HTMLElement, {
      attributes: true,
      attributeFilter: ["data-src", "src"],
      childList: true,
      subtree: true,
    })

    loadHubSpot()
      .then((hbspt) => {
        if (cancelled || createdRef.current) return
        createdRef.current = true
        hbspt.forms.create({
          portalId,
          formId,
          target: `#${elementId}`,
        })
        if (target) allowTermlyHubSpotEmbeds(target)
      })
      .catch((err: unknown) => {
        if (!cancelled) console.error(err)
      })

    return () => {
      cancelled = true
      createdRef.current = false
      observer?.disconnect()
      const target = document.getElementById(elementId)
      if (target) target.innerHTML = ""
    }
  }, [portalId, formId, elementId])

  return <div id={elementId} />
}
